import { DecisaoPrecoVinculoEnum } from '@shared/enums/compras/decisao-preco-vinculo.enum';
import { DecisaoQuantidadeVinculoEnum } from '@shared/enums/compras/decisao-quantidade-vinculo.enum';
import { FaixaSugestaoVinculoEnum } from '@shared/enums/compras/faixa-sugestao-vinculo.enum';
import { TipoRegraConfrontoEnum } from '@shared/enums/compras/tipo-regra-confronto.enum';
import { calcularQuantidadeEstoque, obterFatorConversaoEfetivo } from '@shared/helpers/quantidade-estoque-produto.util';
import { UtilService } from '@shared/services/util.service';
import { NotaFiscalEntradaVinculoAlocacaoRequest } from '@shared/services/requests/orquestrador-compras/nota-fiscal-entrada-vinculo-alocacao.request';
import { NotaFiscalEntradaVinculoItemRequest } from '@shared/services/requests/orquestrador-compras/nota-fiscal-entrada-vinculo-item.request';
import { PedidoElegivelVinculoStruct } from '@shared/services/structs/compras/pedido-elegivel-vinculo.struct';
import { VinculoPedidoNotaItemStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota-item.struct';
import { fmtMoneyDisplay, formatCodigoCompra, nomeFornecedorFallback } from '../card-edit-dialog/card-edit-dialog.types';

/*
 * Estado, view-model e regras do modal de vínculo item da NF × item do pedido.
 * As regras são portadas do protótipo (`concilVals` em Processo de Compras.dc.html: `sugestaoTag`,
 * `alocar`, painel direito) como funções puras — o componente só guarda estado e
 * chama `montarItemVm` / `montarItensRequest`.
 *
 * Quantidades: o contrato aceita 4 casas, então toda conta é arredondada em 4 casas e as
 * comparações são exatas depois do arredondamento (o protótipo usava 3 casas e tolerância 0,001).
 */

export type FiltroVinculo = 'todos' | 'pendentes' | 'vinculados';

export type DecisaoExcesso =
  | DecisaoQuantidadeVinculoEnum.AceitarAjustarSaldo
  | DecisaoQuantidadeVinculoEnum.MarcarDivergencia;

/** Percentual sem valor na etapa `faturado` (ou etapa não carregada) vale o padrão do backend: 5%. */
export const TOLERANCIA_PADRAO = 5;

/**
 * Tolerâncias da etapa `faturado` (`toleranciaPrecoPercentual` e `toleranciaQuantidadePercentual`).
 * Até o percentual (igual passa) o vínculo segue com uma nota discreta; acima, vale a regra da
 * dimensão (`idRegraToleranciaPreco`/`idRegraToleranciaQuantidade`): Avisar (ou nula) pede decisão,
 * Proibir bloqueia o vínculo do item.
 */
export interface ToleranciasVinculo {
  precoPercentual: number;
  quantidadePercentual: number;
  regraPreco: TipoRegraConfrontoEnum;
  regraQuantidade: TipoRegraConfrontoEnum;
}

/** Item de pedido escolhido com preço diferente do da nota — ou que não tem preço. */
export interface DivergenciaPreco {
  opcao: OpcaoItemPedido;
  /** Preço da nota na unidade de estoque (`valorUnitarioComercial / fatorConversao`). */
  precoNota: number;
  /** `null` = pedido sem preço unitário: só avisa, nunca pede decisão. */
  diferencaPercentual: number | null;
  /** Diferença acima da tolerância: o item da nota só fica pronto com a decisão de preço. */
  foraTolerancia: boolean;
}

/** Um item de pedido candidato, achatado a partir dos pedidos carregados. */
export interface OpcaoItemPedido {
  /** `String(idCompraItem)` — chave estável entre pedidos. */
  chave: string;
  idCompra: number;
  idCompraItem: number;
  numeroItem: number;
  pedidoCodigo: string;
  /** `Pedido #123` — mesmo identificador de exibição do card do Kanban. */
  pedidoLabel: string;
  pedidoTitulo: string;
  /** Fornecedor do pedido: diferente do emitente da nota, o candidato mostra o aviso. */
  idFornecedor: number | null;
  /** Faixa da sugestão do pedido (`FaixaSugestaoVinculoEnum`); `null` quando veio só pela busca. */
  faixaSugestao: number | null;
  idProduto: number | null;
  nome: string;
  unidade: string;
  quantidade: number;
  /** Quantidade ainda disponível no item (já descontados vínculos com outras notas). */
  saldo: number;
  /** Preço unitário do item do pedido, na unidade do pedido; nulo ou 0 = pedido sem preço. */
  valorUnitario: number | null;
  busca: string;
  /**
   * Motivo do bloqueio quando o pedido não pode ir para Pedido Faturado (regra de movimentação);
   * `null` no item elegível. Item bloqueado só aparece na seleção, desabilitado — nunca entra na
   * sugestão, na seleção nem na alocação.
   */
  motivoInelegivel: string | null;
}

/** Estado editável de cada item da nota (equivale aos mapas `concil*[it.id]` do protótipo). */
export interface EstadoItemVinculo {
  /** `null` = o usuário nunca mexeu na seleção: vale a sugestão do XML. */
  selecao: string[] | null;
  /** Quantidades digitadas na seleção manual, por chave do item de pedido; `null` = campo limpo (vale o automático). */
  alocEdit: Record<string, number | null>;
  /** Decisão do usuário; dentro da tolerância de quantidade vale `AceitarAjustarSaldo` sem ela. */
  decisaoExcesso: DecisaoExcesso | null;
  /** Decisão do preço fora da tolerância (uma por item da nota, vale para todos os itens de pedido dele). */
  decisaoPreco: DecisaoPrecoVinculoEnum | null;
  parcialOk: boolean;
  confirmado: boolean;
  aberto: boolean;
  selecionando: boolean;
  busca: string;
  /**
   * Seleção, quantidades e decisão vieram do vínculo gravado (Gerenciar vínculo / Trocar NF). A
   * pré-seleção pelo produto não vale para ele: só a do XML continua como "Sugestão".
   */
  isRestaurado: boolean;
}

/**
 * Saldo de cada item do pedido já usado pelos outros itens da NF, por chave: o backend soma as alocações de todos os
 * itens da nota por item do pedido. `anteriores` (itens da nota antes deste, na ordem da nota) limita a sugestão pelo
 * produto e o preenchimento automático; `outros` (todos os demais) limita o saldo na conferência deste item;
 * `excessoOutros` é o excesso que os demais itens levam ao item do pedido (o último alocado de cada um, sem os que
 * marcam divergência), somado ao excesso deste item como no backend.
 */
export interface UsoSaldoOutros {
  anteriores: Record<string, number>;
  outros: Record<string, number>;
  excessoOutros: Record<string, number>;
}

export interface ResultadoAlocacao {
  manual: boolean;
  aloc: Record<string, number>;
  /** Saldo de cada item escolhido descontado o que os outros itens da NF usam dele. */
  disponivel: Record<string, number>;
  soma: number;
  livre: number;
  difNF: number;
  erro: string;
  /** A nota trouxe mais do que todo o saldo marcado. */
  excesso: boolean;
  /** Os itens marcados ficam com saldo além da nota. */
  sobra: boolean;
}

export interface CalculoVinculoItem {
  qtdEstoque: number;
  unidadeEstoque: string;
  sugestao: string[];
  /** De onde veio a sugestão automática: `xPed` do XML, o produto associado ou nenhuma. */
  origemSugestao: 'xml' | 'produto' | null;
  selecao: string[];
  escolhidos: OpcaoItemPedido[];
  alocacao: ResultadoAlocacao;
  /** Excesso da nota sobre o saldo dos itens escolhidos, em % do saldo (2 casas); `null` sem excesso. */
  excessoPercentual: number | null;
  /** Excesso até a tolerância de quantidade: passa como `AceitarAjustarSaldo`, sem botões. */
  excessoDentroTolerancia: boolean;
  /** Decisão de excesso em vigor: a do usuário ou, dentro da tolerância, `AceitarAjustarSaldo`; nula no bloqueio. */
  decisaoExcesso: DecisaoExcesso | null;
  /** Excesso acima da tolerância com a regra de quantidade Proibir: o item não pode ser vinculado. */
  bloqueioQuantidade: boolean;
  /** Confronto de preço com cada item de pedido escolhido; só os com diferença ou sem preço. */
  divergenciasPreco: DivergenciaPreco[];
  /** Algum preço fora da tolerância com a regra de preço Proibir: o item não pode ser vinculado. */
  bloqueioPreco: boolean;
  /** Algum preço fora da tolerância com a regra Avisar: exige `decisaoPreco`. */
  decidirPreco: boolean;
  /**
   * "Marcar divergência" com item do pedido sem quantidade (saldo já relacionado a outro item da nota): o backend
   * exige quantidade em toda alocação e não aceita excesso na divergência; só "Aceitar e ajustar saldo" serve.
   */
  divergenciaSemSaldo: boolean;
  pronto: boolean;
  resolvido: boolean;
}

export interface InfoAssociacaoVm {
  icone: string;
  texto: string;
  titulo: string;
  destaque: boolean;
}

export interface VinculoPedidoVm {
  chave: string;
  pedidoLabel: string;
  /** Nome do fornecedor do pedido (resolvido no lookup de pessoas). */
  fornecedorLabel: string;
  /** Pedido sem fornecedor: o rótulo "Sem fornecedor" sai apagado. */
  semFornecedor: boolean;
  /** Nome do produto do item do pedido; a quantidade sai em `qtdLabel`. */
  itemLabel: string;
  sugerido: boolean;
  /** "Sugestão" / "Manual" / "Confirmado". */
  origemLabel: string;
  origemTooltip: string | null;
  /** Quantidade e unidade do item do pedido ("5,00 G"), na pílula no formato da nota. */
  qtdLabel: string;
  /** Preço unitário do item do pedido; `null` quando o pedido não tem preço (nulo ou 0). */
  valorUnitario: number | null;
  alocEditavel: boolean;
  alocRotulo: string;
  /** Quantidade digitada (ou `null`, campo limpo) ou, sem digitação, a calculada. */
  alocValor: number | null;
  alocSaldo: string;
  alocInvalida: boolean;
  /** Pedido de outro fornecedor que não o emitente da nota: só avisa, não bloqueia. */
  fornecedorDiferente: boolean;
}

export interface CandidatoPedidoVm {
  chave: string;
  pedidoLabel: string;
  pedidoTitulo: string;
  /** Nome do fornecedor do pedido (resolvido no lookup de pessoas). */
  fornecedorLabel: string;
  /** Pedido sem fornecedor: o rótulo "Sem fornecedor" sai apagado. */
  semFornecedor: boolean;
  /** Nome do produto do item do pedido; a quantidade sai à parte, apagada (`qtdLabel`). */
  itemLabel: string;
  qtdLabel: string;
  marcado: boolean;
  compatLabel: string;
  compatClasse: string;
  elegivel: boolean;
  motivoInelegivel: string | null;
  /** Pedido de outro fornecedor que não o emitente da nota: só avisa, não bloqueia. */
  fornecedorDiferente: boolean;
}

export interface ConfrontoVm {
  estado: 'ok' | 'erro' | 'sobra';
  icone: string;
  texto: string;
  badge: string;
  badgeClasse: string;
}

export interface ItemVinculoVm {
  id: number;
  numero: number;
  ancora: string;
  descricao: string;
  metaXml: string;
  qtdNf: string;
  valorUnitario: number | null;
  assoc: { nome: string; codigo: string; infos: InfoAssociacaoVm[] } | null;
  resolvido: boolean;
  podeConfirmar: boolean;
  /** Motivo do "Vincular" desabilitado (tooltip); `null` = liberado. */
  motivoVincular: string | null;
  aberto: boolean;
  flash: boolean;
  vincResumo: string | null;
  selecionando: boolean;
  semPedidoTexto: string;
  vinculos: VinculoPedidoVm[];
  confronto: ConfrontoVm | null;
  /** Nota discreta do excesso dentro da tolerância de quantidade; `null` fora dela ou sem excesso. */
  notaQuantidade: string | null;
  /** Uma faixa âmbar por item de pedido com preço fora da tolerância ou sem preço. */
  confrontosPreco: ConfrontoVm[];
  /** Notas discretas dos preços com diferença dentro da tolerância. */
  notasPreco: string[];
  decidirExcesso: boolean;
  /** Opção já escolhida para o excesso (marcada nos cartões); `null` sem escolha. */
  escolhaExcesso: DecisaoExcesso | null;
  decidirPreco: boolean;
  /** Opção já escolhida para o preço (marcada nos cartões); `null` sem escolha. */
  escolhaPreco: DecisaoPrecoVinculoEnum | null;
  decidirParcial: boolean;
  busca: string;
  buscando: boolean;
  candidatos: CandidatoPedidoVm[];
  selResumo: string;
}

// ---------------------------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------------------------

/**
 * Quantidade pt-BR como o `fmtQ` do protótipo (2 casas), mas aceitando até 4 quando o valor tem —
 * senão uma falta de 0,004 apareceria como "Faltam 0,00".
 */
export function fmtQ(n: number | null | undefined): string {
  return Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** Número sem casas fixas (fator de conversão: "12", "0,5"). */
function fmtNumero(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

/** Valor monetário com "R$", no mesmo critério do card do Kanban (`fmtMoneyDisplay`). */
export function fmtMoeda(n: number | null | undefined): string {
  const valor = fmtMoneyDisplay(n);
  return valor === '—' ? '—' : `R$ ${valor}`;
}

/**
 * Preço unitário do confronto de preço com 2 a 4 casas ("R$ 19,30", "R$ 17,1523"): o mesmo
 * formato `#,##0.00##` das mensagens de preço do backend.
 */
function fmtPreco(n: number): string {
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/** Percentual com até 2 casas, sem zeros à direita ("12,5%", "5%"), como o `0.##` do backend. */
function fmtPercentual(n: number): string {
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

/** Data da API ("2026-03-01" ou ISO) para dd/MM/yyyy, sem deslocar o dia pelo fuso. */
function fmtData(valor: Date | string | null | undefined): string {
  if (!valor) return '';
  const texto = typeof valor === 'string' ? valor : valor.toISOString();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

export function arred4(n: number): number {
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
}

/** `xPed` casa com o código do pedido comparando só os dígitos, sem zeros à esquerda. */
export function normalizaCodigoPedido(valor: string | number | null | undefined): string {
  return String(valor ?? '').replace(/\D/g, '').replace(/^0+/, '');
}

// ---------------------------------------------------------------------------------------------
// Regras
// ---------------------------------------------------------------------------------------------

export function estadoInicialItem(): EstadoItemVinculo {
  return {
    selecao: null,
    alocEdit: {},
    decisaoExcesso: null,
    decisaoPreco: null,
    parcialOk: false,
    confirmado: false,
    aberto: true,
    selecionando: false,
    busca: '',
    isRestaurado: false,
  };
}

/** Mudou seleção ou quantidade: as decisões e a confirmação deixam de valer (`resetDecisoes`). */
export function resetarDecisoes(estado: EstadoItemVinculo): void {
  estado.decisaoExcesso = null;
  estado.decisaoPreco = null;
  estado.parcialOk = false;
  estado.confirmado = false;
}

/**
 * Itens de pedido que podem ser vinculados: só os de pedidos elegíveis, na ordem de carregamento.
 * É a única lista usada pela sugestão do XML, pela seleção e pela alocação.
 */
export function montarOpcoes(pedidos: PedidoElegivelVinculoStruct[]): OpcaoItemPedido[] {
  return achatarPedidos(pedidos.filter(p => p.isElegivel !== false));
}

/**
 * Itens de pedidos bloqueados pela regra de movimentação: aparecem bloqueados, com o
 * motivo. Só exibidos na seleção, desabilitados e depois dos elegíveis.
 */
export function montarOpcoesBloqueadas(pedidos: PedidoElegivelVinculoStruct[]): OpcaoItemPedido[] {
  return achatarPedidos(pedidos.filter(p => p.isElegivel === false));
}

function achatarPedidos(pedidos: PedidoElegivelVinculoStruct[]): OpcaoItemPedido[] {
  const opcoes: OpcaoItemPedido[] = [];
  pedidos.forEach(pedido => {
    const motivoInelegivel = pedido.isElegivel === false
      ? (pedido.motivoInelegivel || 'Este pedido não pode ser movido para Pedido Faturado.')
      : null;
    const pedidoLabel = `Pedido ${formatCodigoCompra(pedido.codigo) || `#${pedido.idCompra}`}`;
    [...(pedido.itens ?? [])]
      .sort((a, b) => a.numeroItem - b.numeroItem)
      .forEach(item => {
        opcoes.push({
          chave: String(item.idCompraItem),
          idCompra: pedido.idCompra,
          idCompraItem: item.idCompraItem,
          numeroItem: item.numeroItem,
          pedidoCodigo: pedido.codigo,
          pedidoLabel,
          pedidoTitulo: pedido.titulo ?? '',
          idFornecedor: pedido.idFornecedor ?? null,
          faixaSugestao: pedido.faixaSugestao ?? null,
          nome: item.nome ?? '',
          unidade: (item.unidade ?? '').toUpperCase(),
          quantidade: item.quantidade ?? 0,
          saldo: arred4(item.saldo ?? 0),
          valorUnitario: item.valorUnitario ?? null,
          idProduto: item.idProduto ?? null,
          busca: [
            pedido.codigo,
            normalizaCodigoPedido(pedido.codigo),
            pedidoLabel,
            pedido.titulo,
            item.nome,
            item.codigoSku,
          ].filter(Boolean).join(' ').toLowerCase(),
          motivoInelegivel,
        });
      });
  });
  return opcoes;
}

/** Quantidade em estoque do item, nas 4 casas das alocações do vínculo (o Orquestrador confere nas mesmas 4). */
export function quantidadeEstoqueItem(item: VinculoPedidoNotaItemStruct): number {
  if (item.quantidadeEstoque != null) return arred4(item.quantidadeEstoque);
  return arred4(calcularQuantidadeEstoque(item.quantidadeComercial ?? 0, item.fatorConversao, item.unidadeComercial,
    item.unidadeEstoque));
}

export function unidadeEstoqueItem(item: VinculoPedidoNotaItemStruct): string {
  return (item.unidadeEstoque || item.unidadeComercial || 'UN').toUpperCase();
}

/**
 * Sugestão pelo XML: `xPed` identifica o pedido e `nItemPed` o item dentro dele. Sem `nItemPed`,
 * só sugere quando aquele pedido tem um único item. Diferente do protótipo, não procura o
 * `nItemPed` em outros pedidos quando o `xPed` não casa — seria um palpite sem base no XML.
 * `opcoes` são só as elegíveis: `xPed` apontando para pedido bloqueado resulta em nenhuma sugestão.
 */
export function sugestaoXml(item: VinculoPedidoNotaItemStruct, opcoes: OpcaoItemPedido[]): string[] {
  const xPed = normalizaCodigoPedido(item.numeroPedidoCompra);
  if (!xPed) return [];
  const primeiro = opcoes.find(o => normalizaCodigoPedido(o.pedidoCodigo) === xPed);
  if (!primeiro) return [];
  const doPedido = opcoes.filter(o => o.idCompra === primeiro.idCompra);
  if (item.numeroItemPedidoCompra != null) {
    const exato = doPedido.find(o => o.numeroItem === item.numeroItemPedidoCompra);
    return exato ? [exato.chave] : [];
  }
  return doPedido.length === 1 ? [doPedido[0].chave] : [];
}

/**
 * Sugestão pelo produto, para item sem sugestão do XML. Com emitente cadastrado na nota, só vale
 * o item de pedido elegível do mesmo produto E do mesmo fornecedor (faixa "fornecedor e produto"):
 * se for exatamente um, ele é a sugestão; pedido de outro fornecedor nunca é pré-marcado, nem
 * sendo a única opção do produto. Sem emitente cadastrado não dá para afirmar diferença de
 * fornecedor: sugere o único item de pedido elegível do mesmo produto. Fora disso nada é
 * pré-marcado — a lista (filtrada pelo produto) deixa o usuário escolher. Item de pedido cujo saldo os
 * itens anteriores da nota já usaram não é sugerido de novo.
 */
export function sugestaoProduto(
  item: VinculoPedidoNotaItemStruct,
  opcoes: OpcaoItemPedido[],
  idPessoaEmitente: number | null,
  usoAnteriores: Record<string, number>,
): string[] {
  if (item.idProduto == null) return [];
  const mesmoProduto = opcoes.filter(o => o.idProduto === item.idProduto
    && arred4(o.saldo - (usoAnteriores[o.chave] ?? 0)) > 0);
  const candidatas = idPessoaEmitente == null
    ? mesmoProduto
    : mesmoProduto.filter(o => o.faixaSugestao === FaixaSugestaoVinculoEnum.FornecedorEProduto);
  return candidatas.length === 1 ? [candidatas[0].chave] : [];
}

/**
 * Sugestão automática do item: a do XML (`xPed`) tem prioridade, qualquer que seja o fornecedor;
 * sem ela, a do produto, que com emitente cadastrado só considera pedidos do mesmo fornecedor.
 */
export function sugestaoItem(
  item: VinculoPedidoNotaItemStruct,
  opcoes: OpcaoItemPedido[],
  idPessoaEmitente: number | null,
  usoAnteriores: Record<string, number>,
): { chaves: string[]; origem: 'xml' | 'produto' | null } {
  const xml = sugestaoXml(item, opcoes);
  if (xml.length) return { chaves: xml, origem: 'xml' };
  const produto = sugestaoProduto(item, opcoes, idPessoaEmitente, usoAnteriores);
  return produto.length ? { chaves: produto, origem: 'produto' } : { chaves: [], origem: null };
}

/**
 * Candidatos exibidos na seleção. Item com sugestão do XML vê tudo (como antes). Sem ela, a lista
 * mostra só os itens de pedido do mesmo produto — elegíveis e bloqueados; se nenhum item de pedido
 * tem esse produto, volta a mostrar tudo, para o usuário não ficar sem saída. A busca filtra dentro
 * do que é mostrado.
 */
export function candidatosDoItem(
  item: VinculoPedidoNotaItemStruct,
  opcoes: OpcaoItemPedido[],
  bloqueadas: OpcaoItemPedido[],
): { elegiveis: OpcaoItemPedido[]; bloqueadas: OpcaoItemPedido[] } {
  if (item.idProduto == null || sugestaoXml(item, opcoes).length) return { elegiveis: opcoes, bloqueadas };
  const mesmoProduto = (o: OpcaoItemPedido) => o.idProduto === item.idProduto;
  const elegiveisProduto = opcoes.filter(mesmoProduto);
  const bloqueadasProduto = bloqueadas.filter(mesmoProduto);
  if (!elegiveisProduto.length && !bloqueadasProduto.length) return { elegiveis: opcoes, bloqueadas };
  return { elegiveis: elegiveisProduto, bloqueadas: bloqueadasProduto };
}

/**
 * Porte do `alocar` do protótipo: na seleção manual (item escolhido pelo usuário ou mais de um
 * item) o que foi digitado prevalece; o resto é preenchido na ordem de seleção, cada item até o
 * saldo que os itens anteriores da nota deixaram, e o que sobra da nota é excesso, que o request
 * leva no último item alocado. A conferência (acima do saldo, sobra) usa o saldo descontado do que
 * todos os outros itens da nota usam. O último item sem saldo (já relacionado a outro item da nota)
 * não é erro: recebe todo o excesso, que vai para a decisão ou a tolerância.
 */
export function alocar(
  qtdEstoque: number,
  escolhidos: OpcaoItemPedido[],
  sugestao: string[],
  alocEdit: Record<string, number | null>,
  uso: UsoSaldoOutros,
): ResultadoAlocacao {
  const manual = escolhidos.length > 1 || escolhidos.some(o => !sugestao.includes(o.chave));
  const edit = manual ? alocEdit : {};
  const digitado = (chave: string): number | null => edit[chave] != null ? arred4(edit[chave]!) : null;

  let resto = qtdEstoque - escolhidos.reduce((t, o) => t + (digitado(o.chave) ?? 0), 0);
  const aloc: Record<string, number> = {};
  const disponivel: Record<string, number> = {};
  escolhidos.forEach(o => {
    disponivel[o.chave] = arred4(Math.max(0, o.saldo - (uso.outros[o.chave] ?? 0)));
    const valor = digitado(o.chave);
    if (valor != null) {
      aloc[o.chave] = valor;
      return;
    }
    const v = arred4(Math.max(0, Math.min(o.saldo - (uso.anteriores[o.chave] ?? 0), resto)));
    aloc[o.chave] = v;
    resto -= v;
  });

  const soma = arred4(escolhidos.reduce((t, o) => t + aloc[o.chave], 0));
  const livre = arred4(escolhidos.reduce((t, o) => t + Math.max(0, disponivel[o.chave] - aloc[o.chave]), 0));
  const difNF = arred4(soma - qtdEstoque);
  const acima = escolhidos.filter(o => arred4(aloc[o.chave] - disponivel[o.chave]) > 0);
  const faltaNF = difNF < 0;
  const ultimo = escolhidos[escolhidos.length - 1];
  const zerado = escolhidos.filter(o => aloc[o.chave] <= 0
    && !(o === ultimo && faltaNF && livre <= 0 && disponivel[o.chave] <= 0));
  const erro = acima.length
    ? `Quantidade acima do saldo de ${acima[0].pedidoLabel} · ${acima[0].nome}.`
    : difNF > 0
      ? `O total relacionado excede a nota em ${fmtQ(difNF)}.`
      : zerado.length
        ? (zerado[0].saldo > 0 && disponivel[zerado[0].chave] <= 0
          ? `O saldo de ${zerado[0].pedidoLabel} · ${zerado[0].nome} já está relacionado a outro item da nota; desmarque o item.`
          : `Informe a quantidade de ${zerado[0].pedidoLabel} · ${zerado[0].nome} ou desmarque o item.`)
        : (faltaNF && livre > 0)
          ? `Faltam ${fmtQ(-difNF)} para relacionar — ainda há saldo nos itens marcados.`
          : '';

  return {
    manual, aloc, disponivel, soma, livre, difNF, erro,
    // Sem item de pedido escolhido não há saldo a exceder: o item só está pendente.
    excesso: !erro && faltaNF && escolhidos.length > 0,
    sobra: !erro && !faltaNF && livre > 0,
  };
}

/** Preço da nota na unidade de estoque; `null` quando a nota não traz preço (não há o que comparar). */
function precoNotaEstoqueItem(item: VinculoPedidoNotaItemStruct): number | null {
  const preco = item.valorUnitarioComercial ?? 0;
  if (preco <= 0) return null;
  // Mesmo fator efetivo do Orquestrador (QuantidadeEstoqueProdutoHelper).
  return preco / obterFatorConversaoEfetivo(item.fatorConversao, item.unidadeComercial, item.unidadeEstoque);
}

/**
 * `parte / base × 100` com 2 casas, no mesmo critério do backend (`Math.Round(..., 2)`, metade para o
 * par = ABNT) antes de comparar com a tolerância. O `toFixed(10)` tira o resíduo de ponto flutuante
 * (12,354999… → 12,355) para a metade ser vista.
 */
function percentual2Casas(parte: number, base: number): number {
  return UtilService.arredondarAbnt(Number((parte / base * 100).toFixed(10)), 2);
}

/**
 * Confronto de preço da nota com cada item de pedido escolhido, pela tolerância da etapa `faturado`:
 * diferença % = |nota − pedido| / pedido × 100, com o preço da nota na unidade de estoque. Diferença 0
 * não entra; até a tolerância (igual passa) fica só a nota discreta; acima, o item da nota pede a
 * decisão de preço. Pedido sem preço (nulo ou 0) só avisa, nunca pede decisão; nota sem preço não é
 * comparada.
 */
export function confrontarPreco(
  item: VinculoPedidoNotaItemStruct,
  escolhidos: OpcaoItemPedido[],
  toleranciaPercentual: number,
): DivergenciaPreco[] {
  const precoNota = precoNotaEstoqueItem(item);
  if (precoNota == null) return [];
  const divergencias: DivergenciaPreco[] = [];
  escolhidos.forEach(o => {
    const precoPedido = o.valorUnitario ?? 0;
    if (precoPedido <= 0) {
      divergencias.push({ opcao: o, precoNota, diferencaPercentual: null, foraTolerancia: false });
      return;
    }
    const diferencaPercentual = percentual2Casas(Math.abs(precoNota - precoPedido), precoPedido);
    if (diferencaPercentual === 0) return;
    divergencias.push({
      opcao: o,
      precoNota,
      diferencaPercentual,
      foraTolerancia: diferencaPercentual > toleranciaPercentual,
    });
  });
  return divergencias;
}

/**
 * Estado de um item no vínculo: seleção efetiva, alocação, tolerâncias de quantidade e de preço e se
 * está pronto/resolvido.
 */
export function calcularVinculoItem(
  item: VinculoPedidoNotaItemStruct,
  estado: EstadoItemVinculo,
  opcoes: OpcaoItemPedido[],
  idPessoaEmitente: number | null,
  tolerancias: ToleranciasVinculo,
  uso: UsoSaldoOutros,
): CalculoVinculoItem {
  const qtdEstoque = quantidadeEstoqueItem(item);
  // Sugestão do XML ou do produto: as duas valem igual para o badge e para a regra do `manual`.
  // Item restaurado do vínculo gravado ignora a do produto: suas linhas são manuais (quantidade gravada).
  const sugestaoAuto = sugestaoItem(item, opcoes, idPessoaEmitente, uso.anteriores);
  const { chaves: sugestao, origem: origemSugestao } = estado.isRestaurado && sugestaoAuto.origem === 'produto'
    ? { chaves: [], origem: null }
    : sugestaoAuto;
  const selecao = estado.selecao ?? sugestao;
  const escolhidos = selecao
    .map(chave => opcoes.find(o => o.chave === chave))
    .filter((o): o is OpcaoItemPedido => !!o);
  const alocacao = alocar(qtdEstoque, escolhidos, sugestao, estado.alocEdit, uso);
  // Excesso, como no backend: o que este item da nota traz além do saldo que sobra para ele, somado ao excesso
  // que os outros itens da nota levam ao mesmo item do pedido (o último alocado, onde o request põe o excesso).
  // A base do % é o saldo dos itens escolhidos sem descontar os outros itens da nota.
  const somaSaldo = arred4(escolhidos.reduce((t, o) => t + o.saldo, 0));
  const ultimo = escolhidos[escolhidos.length - 1];
  const excessoItemPedido = alocacao.excesso
    ? arred4(-alocacao.difNF + (uso.excessoOutros[ultimo.chave] ?? 0))
    : 0;
  const excessoPercentual = alocacao.excesso && somaSaldo > 0
    ? percentual2Casas(excessoItemPedido, somaSaldo)
    : null;
  const excessoDentroTolerancia = excessoPercentual != null && excessoPercentual <= tolerancias.quantidadePercentual;
  // Acima da tolerância, a regra Proibir da etapa bloqueia o item: não há decisão a tomar.
  const bloqueioQuantidade = tolerancias.regraQuantidade === TipoRegraConfrontoEnum.Proibir
    && alocacao.excesso && !excessoDentroTolerancia;
  // Dentro da tolerância o saldo é ajustado sozinho; a decisão já tomada (ou restaurada) prevalece.
  const decisaoExcesso = bloqueioQuantidade
    ? null
    : estado.decisaoExcesso ?? (excessoDentroTolerancia ? DecisaoQuantidadeVinculoEnum.AceitarAjustarSaldo : null);
  const divergenciasPreco = confrontarPreco(item, escolhidos, tolerancias.precoPercentual);
  const precoForaTolerancia = divergenciasPreco.some(d => d.foraTolerancia);
  const bloqueioPreco = tolerancias.regraPreco === TipoRegraConfrontoEnum.Proibir && precoForaTolerancia;
  const decidirPreco = precoForaTolerancia && !bloqueioPreco;
  const divergenciaSemSaldo = alocacao.excesso && decisaoExcesso === DecisaoQuantidadeVinculoEnum.MarcarDivergencia
    && escolhidos.some(o => alocacao.aloc[o.chave] <= 0);
  // Preço fora da tolerância sem decisão segura o item, como o excesso de quantidade sem decisão;
  // com a regra Proibir o item fica bloqueado até a seleção ou as quantidades mudarem.
  const pronto = escolhidos.length > 0 && !alocacao.erro
    && !bloqueioQuantidade && !bloqueioPreco && !divergenciaSemSaldo
    && (alocacao.excesso ? !!decisaoExcesso : true)
    && (alocacao.sobra ? estado.parcialOk : true)
    && (decidirPreco ? !!estado.decisaoPreco : true);
  return {
    qtdEstoque,
    unidadeEstoque: unidadeEstoqueItem(item),
    sugestao,
    origemSugestao,
    selecao,
    escolhidos,
    alocacao,
    excessoPercentual,
    excessoDentroTolerancia,
    decisaoExcesso,
    bloqueioQuantidade,
    divergenciasPreco,
    bloqueioPreco,
    decidirPreco,
    divergenciaSemSaldo,
    pronto,
    // "Resolvido" exige as duas coisas: o usuário ter confirmado E tudo estar decidido.
    resolvido: estado.confirmado && pronto,
  };
}

/**
 * Uso do saldo de cada item do pedido pelos outros itens da NF, na ordem da nota: cada item aloca sobre o
 * saldo que os anteriores deixaram e confere contra o que todos os outros alocaram. O excesso de cada item
 * da nota fica no último item do pedido alocado (como no request); o de "Marcar divergência" não entra na
 * soma dos outros, como no backend.
 */
export function calcularUsoSaldoOutros(
  itens: VinculoPedidoNotaItemStruct[],
  estados: Map<number, EstadoItemVinculo>,
  opcoes: OpcaoItemPedido[],
  idPessoaEmitente: number | null,
  tolerancias: ToleranciasVinculo,
): Map<number, UsoSaldoOutros> {
  const total: Record<string, number> = {};
  const totalExcesso: Record<string, number> = {};
  const porItem = itens.map(item => {
    const anteriores = { ...total };
    const estado = estados.get(item.idNFeEntradaItem) ?? estadoInicialItem();
    const calc = calcularVinculoItem(item, estado, opcoes, idPessoaEmitente, tolerancias,
      { anteriores, outros: anteriores, excessoOutros: {} });
    const { aloc } = calc.alocacao;
    Object.entries(aloc).forEach(([chave, qtd]) => total[chave] = arred4((total[chave] ?? 0) + qtd));
    const excesso: Record<string, number> = {};
    if (calc.alocacao.excesso && estado.decisaoExcesso !== DecisaoQuantidadeVinculoEnum.MarcarDivergencia) {
      const chave = calc.escolhidos[calc.escolhidos.length - 1].chave;
      excesso[chave] = arred4(-calc.alocacao.difNF);
      totalExcesso[chave] = arred4((totalExcesso[chave] ?? 0) + excesso[chave]);
    }
    return { id: item.idNFeEntradaItem, anteriores, aloc, excesso };
  });
  const descontar = (totais: Record<string, number>, proprio: Record<string, number>) =>
    Object.fromEntries(Object.entries(totais).map(([chave, qtd]) => [chave, arred4(qtd - (proprio[chave] ?? 0))]));
  return new Map(porItem.map(({ id, anteriores, aloc, excesso }) => [id, {
    anteriores,
    outros: descontar(total, aloc),
    excessoOutros: descontar(totalExcesso, excesso),
  }]));
}

function textoSemPedido(item: VinculoPedidoNotaItemStruct): string {
  const xPed = (item.numeroPedidoCompra ?? '').trim();
  if (!xPed) return 'O XML não traz a tag xPed — escolha o pedido e o item manualmente.';
  return item.numeroItemPedidoCompra != null
    ? `O XML informa o pedido ${xPed}, item ${item.numeroItemPedidoCompra}, mas o vínculo ainda não foi confirmado.`
    : `O XML informa o pedido ${xPed} sem a tag nItemPed — escolha qual item do pedido corresponde.`;
}

const MAX_LINHAS_TOOLTIP = 20;

function linhasTooltip(linhas: string[]): string {
  if (linhas.length <= MAX_LINHAS_TOOLTIP) return linhas.join('\n');
  return [...linhas.slice(0, MAX_LINHAS_TOOLTIP), `… e mais ${linhas.length - MAX_LINHAS_TOOLTIP}`].join('\n');
}

/** Chips de "ASSOCIADO NO CADASTRO": conversão de unidade, lotes e séries gravados na associação. */
function infosAssociacao(item: VinculoPedidoNotaItemStruct, qtdEstoque: number, unEst: string): InfoAssociacaoVm[] {
  const infos: InfoAssociacaoVm[] = [];
  const fator = obterFatorConversaoEfetivo(item.fatorConversao, item.unidadeComercial, item.unidadeEstoque);
  const unNf = (item.unidadeComercial ?? '').toUpperCase();
  infos.push(fator !== 1
    ? {
      icone: 'swap_horiz',
      texto: `1 ${unNf} = ${fmtNumero(fator)} ${unEst} → ${fmtQ(qtdEstoque)} ${unEst}`,
      titulo: 'Houve conversão de unidade no passo de associação',
      destaque: true,
    }
    : { icone: 'straighten', texto: `Sem conversão · ${unEst}`, titulo: 'A unidade da nota é a mesma do estoque', destaque: false });

  const lotes = (item.lotes ?? []).filter(l => (l.codigo ?? '').trim());
  if (lotes.length) {
    infos.push({
      icone: 'inventory',
      texto: lotes.length === 1 ? `Lote ${lotes[0].codigo}` : `${lotes.length} lotes`,
      titulo: linhasTooltip(lotes.map(l => {
        const validade = fmtData(l.dataValidade);
        return `${l.codigo}${validade ? ' · val. ' + validade : ''} · ${fmtQ(l.quantidade)}`;
      })),
      destaque: false,
    });
  }

  const series = (item.numerosSerie ?? []).filter(s => (s ?? '').trim());
  if (series.length) {
    infos.push({
      icone: 'tag',
      texto: series.length === 1 ? `Série ${series[0]}` : `${series.length} séries`,
      titulo: linhasTooltip(series),
      destaque: false,
    });
  }
  return infos;
}

function montarConfronto(calc: CalculoVinculoItem, toleranciaPercentual: number): ConfrontoVm {
  const a = calc.alocacao;
  const un = calc.unidadeEstoque;
  // Lado do pedido na unidade do próprio item do pedido: unidade diferente da de estoque fica visível.
  const unidadesPedido = [...new Set(calc.escolhidos.map(o => o.unidade).filter(Boolean))];
  const unPedido = unidadesPedido.length === 1 ? unidadesPedido[0] : un;
  const somaSaldo = arred4(calc.escolhidos.reduce((t, o) => t + a.disponivel[o.chave], 0));
  const erro = !!a.erro;
  const bate = !erro && !a.excesso && !a.sobra;
  // Só informação: o saldo do item do pedido é dividido com outros itens da nota (o backend soma por item do pedido).
  const compartilhado = calc.escolhidos.some(o => a.disponivel[o.chave] < o.saldo)
    ? ' Parte do saldo já está relacionada a outro item da nota.'
    : '';
  // Excesso dentro da tolerância passa sozinho: âmbar, como a sobra, e não vermelho.
  const estado: ConfrontoVm['estado'] = bate
    ? 'ok'
    : erro || (a.excesso && !calc.excessoDentroTolerancia) ? 'erro' : 'sobra';
  const texto = (erro
    ? a.erro
    : a.manual
      ? `A nota traz ${fmtQ(calc.qtdEstoque)} ${un} e ${fmtQ(a.soma)} ${unPedido} foram relacionados de ${fmtQ(somaSaldo)} disponíveis nos pedidos.`
      : `A nota traz ${fmtQ(calc.qtdEstoque)} ${un} e o pedido prevê ${fmtQ(somaSaldo)} ${unPedido}.`) + compartilhado;
  // Regra Proibir: o excesso acima da tolerância bloqueia o item, sem cartões de decisão.
  if (calc.bloqueioQuantidade) {
    const percentual = calc.excessoPercentual != null ? ` (${fmtPercentual(calc.excessoPercentual)})` : '';
    return {
      estado: 'erro',
      icone: 'block',
      texto: `${texto} A NF excede em ${fmtQ(-a.difNF)} ${un}${percentual}, acima da tolerância de`
        + ` ${fmtPercentual(toleranciaPercentual)}. A etapa Pedido Faturado proíbe o vínculo fora da tolerância.`,
      badge: 'Bloqueado',
      badgeClasse: 'badge-vermelho',
    };
  }
  return {
    estado,
    icone: estado === 'ok' ? 'check_circle' : estado === 'erro' ? 'error' : 'call_split',
    texto,
    badge: erro
      ? 'Ajuste as quantidades'
      : bate
        ? 'Quantidades batem'
        : a.excesso ? `NF excede em ${fmtQ(-a.difNF)}` : `Sobra ${fmtQ(a.livre)} no pedido`,
    badgeClasse: estado === 'ok' ? 'badge-verde' : estado === 'erro' ? 'badge-vermelho' : 'badge-amarelo',
  };
}

/** Nota discreta do excesso de quantidade dentro da tolerância: o saldo do pedido é ajustado sozinho. */
function montarNotaQuantidade(calc: CalculoVinculoItem, toleranciaPercentual: number): string | null {
  if (!calc.excessoDentroTolerancia || calc.excessoPercentual == null) return null;
  return `NF excede em ${fmtQ(-calc.alocacao.difNF)} ${calc.unidadeEstoque} (${fmtPercentual(calc.excessoPercentual)}),`
    + ` dentro da tolerância de ${fmtPercentual(toleranciaPercentual)}; o saldo do pedido será ajustado.`;
}

/**
 * Uma faixa âmbar por item de pedido com preço fora da tolerância ou sem preço, no visual do
 * confronto de quantidade, com os dois preços na unidade do item do pedido. Depois da decisão o
 * badge mostra o que foi escolhido. Com a regra de preço Proibir, a faixa fora da tolerância fica
 * vermelha e "Bloqueado".
 */
function montarConfrontosPreco(
  calc: CalculoVinculoItem,
  toleranciaPercentual: number,
  decisaoPreco: DecisaoPrecoVinculoEnum | null,
): ConfrontoVm[] {
  return calc.divergenciasPreco
    .filter(d => d.diferencaPercentual == null || d.foraTolerancia)
    .map(d => {
      const un = d.opcao.unidade || calc.unidadeEstoque;
      const precoNota = `${fmtPreco(d.precoNota)}/${un}`;
      if (d.diferencaPercentual == null) {
        return {
          estado: 'sobra',
          icone: 'price_change',
          texto: `O ${d.opcao.pedidoLabel} não tem preço unitário; a nota traz ${precoNota}.`,
          badge: 'Pedido sem preço',
          badgeClasse: 'badge-amarelo',
        };
      }
      // Curto e comparável: os dois preços lado a lado e a diferença com sinal (nota acima ou abaixo do pedido).
      const precoPedido = d.opcao.valorUnitario ?? 0;
      const sinal = d.precoNota > precoPedido ? '+' : '−';
      const texto = `Nota ${precoNota} · ${d.opcao.pedidoLabel} ${fmtPreco(precoPedido)}/${un}`
        + ` (${sinal}${fmtPercentual(d.diferencaPercentual)})`;
      if (calc.bloqueioPreco) {
        return {
          estado: 'erro',
          icone: 'block',
          texto: `${texto}, acima da tolerância de ${fmtPercentual(toleranciaPercentual)}.`
            + ' A etapa Pedido Faturado proíbe o vínculo fora da tolerância.',
          badge: 'Bloqueado',
          badgeClasse: 'badge-vermelho',
        };
      }
      return {
        estado: 'sobra',
        icone: 'price_change',
        texto,
        badge: decisaoPreco === DecisaoPrecoVinculoEnum.AceitarAjustarPreco
          ? 'Ajusta o preço do pedido'
          : decisaoPreco === DecisaoPrecoVinculoEnum.MarcarDivergencia
            ? 'Marca divergência'
            : `Acima da tolerância de ${fmtPercentual(toleranciaPercentual)}`,
        badgeClasse: 'badge-amarelo',
      };
    });
}

/** Notas discretas dos preços com diferença dentro da tolerância: o preço do pedido é mantido. */
function montarNotasPreco(calc: CalculoVinculoItem, toleranciaPercentual: number): string[] {
  return calc.divergenciasPreco
    .filter(d => d.diferencaPercentual != null && !d.foraTolerancia)
    .map(d => {
      const un = d.opcao.unidade || calc.unidadeEstoque;
      const precoPedido = d.opcao.valorUnitario ?? 0;
      const sinal = d.precoNota > precoPedido ? '+' : '−';
      return `Preço da nota ${fmtPreco(d.precoNota)}/${un} · ${d.opcao.pedidoLabel} ${fmtPreco(precoPedido)}/${un}`
        + ` (${sinal}${fmtPercentual(d.diferencaPercentual ?? 0)}), dentro da tolerância de`
        + ` ${fmtPercentual(toleranciaPercentual)}. O preço do pedido é mantido.`;
    });
}

/** Nome do fornecedor do pedido; sem nome resolvido (ainda ou falha no lookup), o fallback com o id. */
function rotuloFornecedor(idFornecedor: number | null, nomesFornecedor: ReadonlyMap<number, string>): string {
  if (idFornecedor == null) return 'Sem fornecedor';
  return nomesFornecedor.get(idFornecedor) || nomeFornecedorFallback(idFornecedor);
}

/** A busca local também casa com o nome do fornecedor já resolvido (o placeholder promete). */
function filtrarCandidatos(
  opcoes: OpcaoItemPedido[],
  busca: string,
  nomesFornecedor: ReadonlyMap<number, string>,
): OpcaoItemPedido[] {
  const termo = busca.trim().toLowerCase();
  if (!termo) return opcoes;
  const termoDigitos = normalizaCodigoPedido(termo);
  return opcoes.filter(o => o.busca.includes(termo)
    || (o.idFornecedor != null && !!nomesFornecedor.get(o.idFornecedor)?.toLowerCase().includes(termo))
    || (!!termoDigitos && /^[#\d\s]+$/.test(termo) && normalizaCodigoPedido(o.pedidoCodigo) === termoDigitos));
}

export function montarItemVm(
  item: VinculoPedidoNotaItemStruct,
  estado: EstadoItemVinculo,
  opcoes: OpcaoItemPedido[],
  bloqueadas: OpcaoItemPedido[],
  flashId: number | null,
  buscandoId: number | null,
  idPessoaEmitente: number | null,
  nomesFornecedor: ReadonlyMap<number, string>,
  tolerancias: ToleranciasVinculo,
  uso: UsoSaldoOutros,
): ItemVinculoVm {
  const calc = calcularVinculoItem(item, estado, opcoes, idPessoaEmitente, tolerancias, uso);
  const a = calc.alocacao;
  const un = calc.unidadeEstoque;
  const temPedido = calc.escolhidos.length > 0;
  // Filtro por produto da seleção; o que já está marcado continua visível para poder desmarcar.
  // Cada grupo por faixa (1 → 2 → 3 → sem faixa); o sort estável mantém a ordem de carga na faixa.
  const doItem = candidatosDoItem(item, opcoes, bloqueadas);
  const porFaixa = (x: OpcaoItemPedido, y: OpcaoItemPedido) =>
    (x.faixaSugestao ?? Number.MAX_SAFE_INTEGER) - (y.faixaSugestao ?? Number.MAX_SAFE_INTEGER);
  const visiveis = {
    elegiveis: opcoes.filter(o => doItem.elegiveis.includes(o) || calc.selecao.includes(o.chave)).sort(porFaixa),
    bloqueadas: [...doItem.bloqueadas].sort(porFaixa),
  };
  const unNf = (item.unidadeComercial ?? '').toUpperCase();
  const meta = [
    item.codigoProdutoEmitente ? `Cód. ${item.codigoProdutoEmitente}` : '',
    item.codigoEan ? `EAN ${item.codigoEan}` : '',
    item.codigoNcm ? `NCM ${item.codigoNcm}` : '',
  ].filter(Boolean).join(' · ');
  const temAssoc = item.idProduto != null || !!item.nomeProduto;

  return {
    id: item.idNFeEntradaItem,
    numero: item.numeroItem,
    ancora: `vinc-item-${item.idNFeEntradaItem}`,
    descricao: item.descricao ?? '',
    metaXml: meta,
    qtdNf: `${fmtQ(item.quantidadeComercial)} ${unNf}`.trim(),
    valorUnitario: item.valorUnitarioComercial,
    assoc: temAssoc
      ? {
        nome: item.nomeProduto ?? '',
        codigo: item.codigoProduto ?? '',
        infos: infosAssociacao(item, calc.qtdEstoque, un),
      }
      : null,
    resolvido: calc.resolvido,
    podeConfirmar: calc.pronto,
    // Mesma ordem das condições do `pronto`.
    motivoVincular: calc.resolvido
      ? 'Item já vinculado.'
      : !temPedido
        ? 'Escolha o item do pedido que esta linha da nota atende.'
        : a.erro
          ? a.erro
          : calc.bloqueioQuantidade && calc.bloqueioPreco
            ? 'Quantidade e preço fora da tolerância: a etapa Pedido Faturado proíbe o vínculo.'
            : calc.bloqueioQuantidade
              ? 'Quantidade fora da tolerância: a etapa Pedido Faturado proíbe o vínculo.'
              : calc.bloqueioPreco
                ? 'Preço fora da tolerância: a etapa Pedido Faturado proíbe o vínculo.'
                : a.excesso && !calc.decisaoExcesso
                  ? 'A nota excede o saldo do pedido: escolha Aceitar e ajustar saldo ou Marcar divergência.'
                  : calc.divergenciaSemSaldo
                    ? 'O saldo do item do pedido já está relacionado a outro item da nota: escolha Aceitar e ajustar saldo.'
                    : a.sobra && !estado.parcialOk
                      ? 'Sobra saldo no pedido: escolha Receber parcial e dividir o saldo.'
                      : calc.decidirPreco && !estado.decisaoPreco
                        ? 'Preço fora da tolerância: escolha Aceitar e ajustar preço ou Marcar divergência.'
                        : null,
    aberto: estado.aberto,
    flash: flashId === item.idNFeEntradaItem,
    vincResumo: !temPedido
      ? null
      : calc.escolhidos.length === 1
        ? `${calc.escolhidos[0].pedidoLabel} · ${calc.escolhidos[0].nome}`
        : `${calc.escolhidos.length} itens de pedido`,
    selecionando: estado.selecionando,
    semPedidoTexto: textoSemPedido(item),
    vinculos: calc.escolhidos.map(o => {
      const alocado = a.aloc[o.chave] ?? 0;
      const disponivel = a.disponivel[o.chave];
      const restante = arred4(disponivel - alocado);
      return {
        chave: o.chave,
        pedidoLabel: o.pedidoLabel,
        fornecedorLabel: rotuloFornecedor(o.idFornecedor, nomesFornecedor),
        semFornecedor: o.idFornecedor == null,
        itemLabel: o.nome,
        sugerido: calc.sugestao.includes(o.chave),
        origemLabel: estado.confirmado
          ? 'Confirmado'
          : !calc.sugestao.includes(o.chave) ? 'Manual' : 'Sugestão',
        origemTooltip: !calc.sugestao.includes(o.chave)
          ? (estado.isRestaurado ? 'Vínculo gravado anteriormente' : null)
          : calc.origemSugestao === 'produto' ? 'Mesmo produto associado ao item da nota' : 'Sugerido pelo XML (xPed/nItemPed)',
        qtdLabel: `${fmtQ(o.quantidade)} ${o.unidade}`,
        valorUnitario: o.valorUnitario ? o.valorUnitario : null,
        alocEditavel: a.manual,
        alocRotulo: `Qtd. a relacionar (${un})`,
        alocValor: o.chave in estado.alocEdit ? estado.alocEdit[o.chave] : alocado,
        alocSaldo: `de ${fmtQ(disponivel)} ${un} disponíveis` + (restante > 0 ? ` · saldo ${fmtQ(restante)} fica no pedido` : ''),
        alocInvalida: arred4(alocado - disponivel) > 0 || alocado <= 0,
        // Mesma regra dos candidatos: sem emitente cadastrado não dá para afirmar a diferença.
        fornecedorDiferente: idPessoaEmitente != null
          && (o.faixaSugestao === FaixaSugestaoVinculoEnum.SomenteProduto
            || (o.faixaSugestao == null && o.idFornecedor !== idPessoaEmitente)),
      };
    }),
    confronto: temPedido ? montarConfronto(calc, tolerancias.quantidadePercentual) : null,
    notaQuantidade: montarNotaQuantidade(calc, tolerancias.quantidadePercentual),
    confrontosPreco: montarConfrontosPreco(calc, tolerancias.precoPercentual, estado.decisaoPreco),
    notasPreco: montarNotasPreco(calc, tolerancias.precoPercentual),
    // Excesso dentro da tolerância já tem decisão (automática): as opções só aparecem acima dela. Ficam visíveis
    // depois da escolha (marcada) para dar para trocar, até o item ser confirmado. Bloqueado (Proibir) não decide.
    decidirExcesso: temPedido && a.excesso && !calc.excessoDentroTolerancia && !calc.bloqueioQuantidade && !estado.confirmado,
    escolhaExcesso: estado.decisaoExcesso,
    decidirPreco: calc.decidirPreco && !estado.confirmado,
    escolhaPreco: estado.decisaoPreco,
    decidirParcial: temPedido && a.sobra && !estado.parcialOk,
    busca: estado.busca,
    buscando: buscandoId === item.idNFeEntradaItem,
    // Elegíveis primeiro, depois os bloqueados, desabilitados e com o motivo; cada grupo por faixa.
    candidatos: estado.selecionando
      ? [
        ...filtrarCandidatos(visiveis.elegiveis, estado.busca, nomesFornecedor),
        ...filtrarCandidatos(visiveis.bloqueadas, estado.busca, nomesFornecedor),
      ].map(o => {
        const elegivel = o.motivoInelegivel == null;
        // Saldo que os outros itens da nota deixam neste item do pedido.
        const d = arred4(o.saldo - (uso.outros[o.chave] ?? 0) - calc.qtdEstoque);
        return {
          chave: o.chave,
          pedidoLabel: o.pedidoLabel,
          pedidoTitulo: o.pedidoTitulo,
          fornecedorLabel: rotuloFornecedor(o.idFornecedor, nomesFornecedor),
          semFornecedor: o.idFornecedor == null,
          itemLabel: o.nome,
          qtdLabel: `${fmtQ(o.quantidade)} ${o.unidade} no pedido`,
          marcado: elegivel && calc.selecao.includes(o.chave),
          compatLabel: d === 0 ? 'Quantidade bate' : d > 0 ? `Sobra ${fmtQ(d)} no pedido` : `NF excede em ${fmtQ(-d)}`,
          compatClasse: d === 0 ? 'badge-verde' : d > 0 ? 'badge-amarelo' : 'badge-vermelho',
          elegivel,
          motivoInelegivel: o.motivoInelegivel,
          // Nota sem emitente cadastrado não permite afirmar que o fornecedor é diferente.
          fornecedorDiferente: idPessoaEmitente != null
            && (o.faixaSugestao === FaixaSugestaoVinculoEnum.SomenteProduto
              || (o.faixaSugestao == null && o.idFornecedor !== idPessoaEmitente)),
        };
      })
      : [],
    selResumo: calc.escolhidos.length === 0
      ? 'Nenhum item marcado'
      : `${calc.escolhidos.length === 1 ? '1 item marcado' : calc.escolhidos.length + ' itens marcados'}`
        + ` · ${fmtQ(a.soma)} de ${fmtQ(calc.qtdEstoque)} ${un} relacionados`,
  };
}

/**
 * Monta os itens do request do Concluir:
 * - sem decisão ou `DividirSaldo`: soma = `quantidadeEstoque`;
 * - `AceitarAjustarSaldo` (escolhida ou automática dentro da tolerância): soma = `quantidadeEstoque`,
 *   o excedente vai no último item alocado;
 * - `MarcarDivergencia`: as alocações como estão (soma < `quantidadeEstoque`).
 * Fora da divergência, o último item absorve só o resíduo de arredondamento (4 casas), para a
 * soma fechar exatamente com a nota. A decisão de preço só vai quando algum preço passou da tolerância.
 */
export function montarItensRequest(
  itens: VinculoPedidoNotaItemStruct[],
  estados: Map<number, EstadoItemVinculo>,
  opcoes: OpcaoItemPedido[],
  idPessoaEmitente: number | null,
  tolerancias: ToleranciasVinculo,
): NotaFiscalEntradaVinculoItemRequest[] {
  const usos = calcularUsoSaldoOutros(itens, estados, opcoes, idPessoaEmitente, tolerancias);
  return itens.map(item => {
    const estado = estados.get(item.idNFeEntradaItem) ?? estadoInicialItem();
    const calc = calcularVinculoItem(item, estado, opcoes, idPessoaEmitente, tolerancias, usos.get(item.idNFeEntradaItem)!);
    const a = calc.alocacao;
    const alocacoes: NotaFiscalEntradaVinculoAlocacaoRequest[] = calc.escolhidos.map(o => ({
      idCompraItem: o.idCompraItem,
      quantidadeVinculada: arred4(a.aloc[o.chave] ?? 0),
    }));

    let decisao: DecisaoQuantidadeVinculoEnum | null = null;
    if (a.excesso) decisao = calc.decisaoExcesso;
    else if (a.sobra) decisao = DecisaoQuantidadeVinculoEnum.DividirSaldo;

    if (alocacoes.length && decisao !== DecisaoQuantidadeVinculoEnum.MarcarDivergencia) {
      const ultimo = alocacoes[alocacoes.length - 1];
      const outros = alocacoes.slice(0, -1).reduce((t, x) => t + x.quantidadeVinculada, 0);
      ultimo.quantidadeVinculada = arred4(calc.qtdEstoque - outros);
    }

    return {
      idNFeEntradaItem: item.idNFeEntradaItem,
      idDecisaoQuantidadeVinculo: decisao,
      idDecisaoPrecoVinculo: calc.decidirPreco ? estado.decisaoPreco : null,
      alocacoes,
    };
  });
}
